import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, GoldButton } from "@/components/Ui";
import { Building2, Power } from "lucide-react";

const FLAGS_META = [
    { key: "ai_assistant", label: "AI Legal Assistant" },
    { key: "ai_summary", label: "AI Case/Doc Summary" },
    { key: "automations", label: "Automation Builder" },
    { key: "client_portal", label: "Client Portal" },
    { key: "communications_email", label: "Email send" },
    { key: "communications_whatsapp", label: "WhatsApp send" },
    { key: "razorpay_payments", label: "Razorpay payments" },
    { key: "white_label", label: "White label" },
    { key: "webauthn_login", label: "Fingerprint login (beta)" },
];

export default function AdminFeatures() {
    const [tenants, setTenants] = useState([]);
    const [active, setActive] = useState(null); // selected tenant
    const [flags, setFlags] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/tenants").then(r => setTenants(r.data));
    }, []);

    const pick = async (t) => {
        setActive(t);
        // We don't have a per-tenant-flag GET; reuse merged /features by impersonate
        // Simpler: use the stored t.flags if present
        setFlags(t.flags || {});
    };

    const save = async () => {
        if (!active) return;
        setSaving(true);
        try {
            await api.patch(`/platform/tenants/${active.id}/flags`, { flags });
            toast.success(`Flags saved for ${active.name}`);
            api.get("/tenants").then(r => setTenants(r.data));
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const impersonate = async (tenantId) => {
        // Login as the first firm_owner of this tenant
        const t = tenants.find(x => x.id === tenantId);
        if (!window.confirm(`Sign in as the firm owner of "${t.name}"? You'll switch out of super admin.`)) return;
        try {
            // Find owner user via tenant.owner_user_id is not available; use email pattern from seed for demo.
            // For arbitrary tenants, super admin must enter the user ID below — for v1 we use the impersonate endpoint with the firm_owner user id.
            // Quick path: fetch users by tenant — list_tenant_users requires tenant role. So we expose super admin path via /platform/audit which has user_email.
            // Implementation: prompt the super admin for the user_id to impersonate.
            const uid = window.prompt("Enter the user_id to impersonate (firm owner usually):");
            if (!uid) return;
            const { data } = await api.post(`/platform/impersonate/${uid}`);
            toast.success(`Signed in as ${data.user.email}`);
            window.location.href = data.user.role === "client_portal" ? "/portal" : "/app";
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <>
            <PageHeader title="Feature flags" subtitle="Toggle features per tenant. Defaults are inherited from the plan."/>
            <div className="grid lg:grid-cols-[320px_1fr] gap-6">
                <aside>
                    <div className="overline mb-3">Tenants</div>
                    <div className="space-y-1">
                        {tenants.map(t => (
                            <button key={t.id} onClick={() => pick(t)} data-testid={`features-tenant-${t.id}`}
                                className={`w-full text-left p-3 border ${active?.id === t.id ? "border-primary bg-muted" : "border-border bg-muted hover:border-muted-foreground"} transition-colors`}>
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <Building2 className="w-4 h-4 text-primary shrink-0" strokeWidth={1.5}/>
                                        <div className="min-w-0">
                                            <div className="text-sm truncate">{t.name}</div>
                                            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t.status}</div>
                                        </div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </aside>
                <section>
                    {!active ? (
                        <div className="border border-dashed border-border py-20 text-center text-muted-foreground text-sm">Pick a tenant to edit its feature flags.</div>
                    ) : (
                        <div className="bg-muted border border-border p-6">
                            <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                                <div>
                                    <div className="overline">Editing</div>
                                    <div className="font-serif text-xl">{active.name}</div>
                                </div>
                                <button onClick={() => impersonate(active.id)} data-testid={`features-impersonate-${active.id}`}
                                    className="text-xs border border-border hover:border-primary hover:text-primary px-4 py-2 inline-flex items-center gap-2 transition-colors">
                                    <Power className="w-3 h-3"/> Sign in as a user
                                </button>
                            </div>
                            <div className="divide-y divide-border">
                                {FLAGS_META.map(f => (
                                    <label key={f.key} className="flex items-center justify-between py-3 cursor-pointer">
                                        <div className="text-sm">{f.label}</div>
                                        <input type="checkbox" checked={flags[f.key] !== false}
                                            onChange={e => setFlags({ ...flags, [f.key]: e.target.checked })} data-testid={`flag-${f.key}`}
                                            className="w-4 h-4 accent-primary"/>
                                    </label>
                                ))}
                            </div>
                            <div className="mt-6 flex justify-end">
                                <GoldButton onClick={save} disabled={saving} data-testid="features-save-btn">{saving ? "Saving…" : "Save flags"}</GoldButton>
                            </div>
                        </div>
                    )}
                </section>
            </div>
        </>
    );
}
